// Compact "at a glance" sidebar shown next to the datatalk chat (PredictionAnalysisPanel):
// upcoming prediction chips (source/target-date + severity) and the top non-neutral SHAP
// signals, so a professional can see the key facts without leaving the chat view.
import { useMemo } from "react"
import {
    AnalysisServiceGetPHQ8LocalShapResponsePayload,
    AnalysisServiceGetPHQ8PredictionResponsePayload,
    PHQ8ModelMetadata,
    PHQ8Prediction,
    Phq8SeverityKey,
    RankedSignalDto,
    SeverityScore,
    ValueType,
    getSeverityColor,
    getSeverityScore,
} from "pokal_api"
import PokalColorConverter from "../../../util/PokalColorConverter"
import severityToString from "../../../util/severityToString"
import { directionMark, featureLabel } from "../shap/shapUtils"

type Props = {
    metadata: PHQ8ModelMetadata | null
    predictions: AnalysisServiceGetPHQ8PredictionResponsePayload | null
    localShap: AnalysisServiceGetPHQ8LocalShapResponsePayload | null
}

function formatDayMonth(targetDate: string): string {
    // target_date is YYYY-MM-DD; parse in UTC then format DD.MM.
    const d = new Date(`${targetDate}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return targetDate
    return d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        timeZone: "UTC",
    })
}

const SEVERITY_KEY_TO_SCORE: Record<Phq8SeverityKey, SeverityScore> = {
    minimal: 1,
    leicht: 2,
    mittel: 3,
    mittelschwer: 4,
    schwer: 5,
}


function severityScoreFromPrediction(prediction: PHQ8Prediction): SeverityScore {
    if (prediction.severity) return SEVERITY_KEY_TO_SCORE[prediction.severity.key]
    const rounded = Math.max(0, Math.min(24, Math.round(prediction.prediction)))
    return getSeverityScore(
        { date: new Date(), value: rounded, id: "" },
        ValueType.PHQUESTIONNAIRE,
    )
}

function PredictionChip({ prediction }: { prediction: PHQ8Prediction }) {
    const score = severityScoreFromPrediction(prediction)
    const label = severityToString(score, ValueType.PHQUESTIONNAIRE)
    const color = getSeverityColor(score, ValueType.PHQUESTIONNAIRE)
    const chipClass = PokalColorConverter.toBadge(color)

    return (
        <div className={"flex flex-col items-center gap-0.5"}>
            <span className={"text-[10px] text-gray-500"}>
                {formatDayMonth(prediction.target_date)}
            </span>
            <span className={chipClass}>
                {prediction.prediction.toFixed(1)} · {label}
            </span>
        </div>
    )
}

export default function VorhersageKontextPanel({
    metadata,
    predictions,
    localShap,
}: Props) {
    const todayString = new Date().toISOString().split("T")[0]

    const futurePredictions = useMemo(
        () =>
            (predictions?.predictions ?? [])
                .filter(
                    (p) =>
                        typeof p.prediction === "number" && p.target_date >= todayString,
                )
                .sort((a, b) => a.target_date.localeCompare(b.target_date)),
        [predictions, todayString],
    )

    // Read the pre-derived signal list from the local-SHAP response.  Python
    // already ranked + classified — the orient panel just picks the
    // non-neutral ones to surface as quick driver chips.
    const signals: RankedSignalDto[] = localShap?.signals ?? []
    const topDrivers = signals.filter((s) => s.direction !== "neutral")

    if (!metadata || !predictions) {
        return (
            <div className={"flex flex-col gap-3"}>
                <div className={"h-20 animate-pulse rounded-lg bg-gray-100"} />
                <div className={"h-16 animate-pulse rounded-lg bg-gray-100"} />
            </div>
        )
    }

    return (
        <div className={"flex flex-col gap-4"}>
            <section className={"flex flex-col gap-2"}>
                <h3
                    className={
                        "text-xs font-semibold uppercase tracking-wide text-gray-500"
                    }
                >
                    {futurePredictions.length > 0
                        ? `Vorhersage · nächste ${futurePredictions.length} ${futurePredictions.length === 1 ? "Tag" : "Tage"}`
                        : "Vorhersage"}
                </h3>
                {futurePredictions.length > 0 ? (
                    <div className={"flex flex-wrap gap-2"}>
                        {futurePredictions.map((p) => (
                            <PredictionChip
                                key={`${p.source_date}__${p.target_date}`}
                                prediction={p}
                            />
                        ))}
                    </div>
                ) : (
                    <p className={"text-xs text-gray-500"}>
                        Keine Vorhersagen für die kommenden Tage verfügbar.
                    </p>
                )}
            </section>

            <hr className={"border-gray-200"} />

            <section className={"flex flex-col gap-2"}>
                <h3
                    className={
                        "text-xs font-semibold uppercase tracking-wide text-gray-500"
                    }
                >
                    Stärkste Einflüsse
                </h3>
                {topDrivers.length > 0 ? (
                    <ul className={"flex flex-col gap-1"}>
                        {topDrivers.map((signal) => {
                            const mark = directionMark(signal)
                            return (
                                <li
                                    key={signal.groupKey}
                                    className={"flex items-center gap-2 text-sm"}
                                >
                                    <span
                                        className={`text-base leading-none ${mark.colorClass}`}
                                        aria-hidden="true"
                                    >
                                        {mark.symbol}
                                    </span>
                                    <span className={"text-gray-800"}>
                                        {featureLabel(signal.groupKey)}
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <p className={"text-xs text-gray-500"}>
                        Aktuell kein Faktor mit nennenswertem Einfluss.
                    </p>
                )}
            </section>
        </div>
    )
}
